import { z } from 'zod';
export const NODE_ENVS = ['development', 'test', 'production'];
export const LOG_LEVELS = ['fatal', 'error', 'warn', 'info', 'debug', 'trace', 'silent'];
/**
 * `.env.example` ships every key with a blank value, so an unset variable usually arrives as an
 * empty string rather than as `undefined`. Normalise both to `undefined` so `.default()` and
 * `.optional()` behave the way the reader expects.
 */
function blankToUndefined(value) {
    return typeof value === 'string' && value.trim() === '' ? undefined : value;
}
/**
 * A URL constrained to an explicit set of protocols. Zod's own URL check does not distinguish
 * `postgres://` from `https://`, and getting those the wrong way round is a configuration bug
 * worth catching at boot.
 */
function urlWithProtocol(protocols, label) {
    const allowed = new Set(protocols);
    return z
        .string()
        .min(1)
        .refine((value) => {
        try {
            return allowed.has(new URL(value).protocol);
        }
        catch {
            return false;
        }
    }, { message: `must be a valid ${label} URL (${protocols.map((p) => `${p}//`).join(' or ')})` });
}
const httpUrl = urlWithProtocol(['http:', 'https:'], 'http');
const postgresUrl = urlWithProtocol(['postgres:', 'postgresql:'], 'postgres connection');
const optionalText = z.preprocess(blankToUndefined, z.string().min(1).optional());
const optionalHttpUrl = z.preprocess(blankToUndefined, httpUrl.optional());
function text(defaultValue) {
    return z.preprocess(blankToUndefined, z.string().min(1).default(defaultValue));
}
function integer(defaultValue, min, max) {
    return z.preprocess(blankToUndefined, z.coerce.number().int().min(min).max(max).default(defaultValue));
}
/** Like {@link integer}, for a price per million tokens, which is not a whole number. */
function decimal(defaultValue, min, max) {
    return z.preprocess(blankToUndefined, z.coerce.number().min(min).max(max).default(defaultValue));
}
/**
 * A comma-separated role list, normalised to lowercase.
 *
 * Case-folded because an identity provider's own casing is not a contract: Endeavor X issues
 * `admin`, but a role that arrived as `Admin` must not silently fail to match an entry in
 * `EX_ADMIN_ROLES`.
 */
function roleList(defaultValue) {
    return z.preprocess(blankToUndefined, z.string().default(defaultValue)).transform((value) => value
        .split(',')
        .map((role) => role.trim().toLowerCase())
        .filter((role) => role.length > 0));
}
function flag(defaultValue) {
    const fallback = defaultValue ? 'true' : 'false';
    return z
        .preprocess(blankToUndefined, z.enum(['true', 'false']).default(fallback))
        .transform((value) => value === 'true');
}
export const envSchema = z
    .object({
    // --- core ---
    NODE_ENV: z.preprocess(blankToUndefined, z.enum(NODE_ENVS).default('development')),
    LOG_LEVEL: z.preprocess(blankToUndefined, z.enum(LOG_LEVELS).default('info')),
    API_PORT: integer(8080, 1, 65535),
    WEB_ORIGIN: z.preprocess(blankToUndefined, httpUrl.default('http://localhost:5173')),
    // --- database ---
    DATABASE_URL: postgresUrl,
    /**
     * Connections in the API's pool. One pool per process, created at boot and closed on
     * shutdown. The ceiling that matters is the server's `max_connections` divided by the number
     * of replicas, so this is configuration rather than a constant.
     */
    DB_POOL_MAX: integer(10, 1, 100),
    // --- Azure OpenAI ---
    AZURE_OPENAI_ENDPOINT: z.preprocess(blankToUndefined, httpUrl),
    /** Blank means "use Managed Identity" — the expected configuration in Azure. */
    AZURE_OPENAI_API_KEY: optionalText,
    AZURE_OPENAI_API_VERSION: text('2024-10-21'),
    AZURE_OPENAI_CHAT_DEPLOYMENT: text('gpt-4.1'),
    AZURE_OPENAI_EMBEDDING_DEPLOYMENT: text('text-embedding-3-large'),
    EMBEDDING_DIMENSIONS: integer(1024, 1, 4096),
    // --- retrieval: pgvector / HNSW ---
    // Build-time knobs. Changing either only takes effect on the next `pnpm ingest index --build`,
    // because an HNSW index cannot be re-parameterised in place.
    HNSW_M: integer(16, 2, 100),
    HNSW_EF_CONSTRUCTION: integer(64, 4, 1000),
    /** Query-time candidate list. Higher is more accurate and slower; pgvector's own default is 40. */
    HNSW_EF_SEARCH: integer(40, 1, 1000),
    /** The agentic path issues few, expensive searches and can afford a wider scan. */
    HNSW_EF_SEARCH_AGENTIC: integer(100, 1, 1000),
    /**
     * Per-arm weight in Reciprocal Rank Fusion, `score = Σ wᵢ/(k + rankᵢ)`.
     *
     * Configurable because the balance between the two arms is a property of the corpus, not of
     * the technique, and the golden query set in `eval/` is what measures it.
     *
     * **Both default to 1.0, which is unweighted RRF and exactly prompt 06's behaviour.** The
     * knob exists because the golden set has something to say and the decision is not mine to
     * take: over a sweep of 1 / 0.5 / 0.25 / 0.1 / 0.05 / 0.02 nothing regresses at any value and
     * four metrics improve monotonically — course-tier r@5 0.900 → 0.925, MRR 0.945 → 0.982,
     * nDCG 0.898 → 0.916, lesson-tier MRR 0.900 → 1.000. `eval/README.md` has the table.
     *
     * What the sweep also shows is that it cannot fix the case it was reached for. A document
     * found by both arms outranks one found at rank 1 by a single arm for every `k`, and flipping
     * that for the measured example needs `w_lexical < 0.047` — a boundary set by that one
     * query's vector ranks rather than by anything about the catalog, and low enough that the
     * lexical arm has stopped contributing to ranking at all. Changing the default is therefore
     * a decision about retrieval architecture, not a tuning fix, so the default is unchanged.
     *
     * The weight governs **ranking only**. The lexical arm's other three jobs do not depend on
     * it: pool membership (which decides what the vector arm's 80-row window would have missed,
     * and at the lesson tier that is 80 of 28,185 chunks), the `matchedOn` signal the agent
     * reads, and degraded mode, where it is the only arm and its weight cancels.
     */
    RRF_WEIGHT_VECTOR: decimal(1, 0, 100),
    RRF_WEIGHT_LEXICAL: decimal(1, 0, 100),
    // --- rate limits ---
    /**
     * Requests per minute, per IP for the two read limits and per *user* for the agentic ones.
     *
     * Configuration rather than constants for one reason: `pnpm eval:load` cannot measure
     * anything through them. Fifty concurrent callers against a 100/minute bucket produce a
     * second of latency figures and then a wall of 429s, which measures the rate limiter and
     * says nothing about whether the database holds up — and "the database holds up under load"
     * is the question prompt 13 asks. The defaults are the values prompt 07 set and prompt 08
     * tightened; a load run raises them deliberately and the report records which numbers were
     * in force.
     */
    RATE_LIMIT_GLOBAL_PER_MINUTE: integer(100, 1, 1_000_000),
    RATE_LIMIT_SEARCH_PER_MINUTE: integer(30, 1, 1_000_000),
    RATE_LIMIT_AGENT_PER_MINUTE: integer(10, 1, 1_000_000),
    RATE_LIMIT_AGENT_PER_HOUR: integer(100, 1, 1_000_000),
    /**
     * Sign-in, which has its own two buckets and needs them.
     *
     * Per email **and** per IP: neither alone is sufficient (per-email cannot see password
     * spraying across many accounts; per-IP cannot see a distributed attack on one). Five per
     * email per minute is also what makes the refusal enumeration-safe — it fires on the sixth
     * attempt whether or not the address exists, which a database lockout could never do.
     *
     * Configurable for the same reason as the others, and this is the one that actually blocked
     * the e2e suite: it signs an account in once per test, which is more than five a minute
     * inside a single spec file, and the refusal arrives as "heading Courses not visible".
     */
    RATE_LIMIT_LOGIN_PER_EMAIL_PER_MINUTE: integer(5, 1, 1_000_000),
    RATE_LIMIT_LOGIN_PER_IP_PER_MINUTE: integer(100, 1, 1_000_000),
    // --- cost reporting ---
    /**
     * List price per million tokens, in US dollars, used only to turn the token counts in
     * `search_queries` into the cost estimate on `/admin/queries`.
     *
     * Configuration rather than a constant because it is the one number in this file that
     * changes without any code changing — and because an estimate priced at a rate nobody can
     * see or correct is worse than no estimate. The defaults are `gpt-4.1` list pricing at the
     * time of writing; they know nothing about committed throughput or regional pricing, which
     * is why the figure is labelled an estimate everywhere it appears.
     */
    COST_PER_MILLION_INPUT_TOKENS_USD: decimal(2, 0, 10_000),
    COST_PER_MILLION_OUTPUT_TOKENS_USD: decimal(8, 0, 10_000),
    /**
     * `maintenance_work_mem` for the index build only, applied with `set_config` on a pinned
     * connection.
     *
     * Two ceilings, and the build fails differently against each. A parallel HNSW build puts its
     * graph in a dynamic *shared memory* segment sized from this value, so it must fit in the
     * server's shared memory — on Docker that is `shm_size`, 64 MB by default — or the build dies
     * with `53100 could not resize shared memory segment`. And that segment is real memory, so it
     * must also fit in the server's RAM, or the OOM killer takes the whole postmaster down
     * mid-build. 512 MB comfortably holds the graph for 28,185 chunks at 1,024 dimensions and
     * leaves room on a 2 GB container; a larger server should raise it rather than accept the
     * on-disk merge that a graph which does not fit falls back to.
     */
    INDEX_MAINTENANCE_WORK_MEM: z.preprocess(blankToUndefined, z
        .string()
        .regex(/^\d+(?:kB|MB|GB)$/u, 'must be a Postgres memory size such as 512MB or 2GB')
        .default('512MB')),
    // --- auth: Endeavor X OIDC federation ---
    EX_ISSUER: optionalHttpUrl,
    EX_CLIENT_ID: optionalText,
    EX_CLIENT_SECRET: optionalText,
    EX_REDIRECT_URI: z.preprocess(blankToUndefined, httpUrl.default('http://localhost:8080/auth/ex/callback')),
    EX_ADMIN_ROLES: roleList('admin,maintainer'),
    /**
     * Endeavor X roles that are refused a session outright.
     *
     * Empty by default, which admits every federated role — including `guest` — as a `member`.
     * That is a policy decision rather than an oversight: a `member` can read the licensed
     * HCLTech transcripts (00-context §10.1), so adding `guest` here is the switch to flip if
     * that is ever reconsidered, and it is deliberately an environment variable so flipping it
     * is a configuration change and not a release.
     */
    EX_DENIED_ROLES: roleList(''),
    // --- auth: sessions + local accounts ---
    SESSION_COOKIE_NAME: text('catalog_session'),
    SESSION_TTL_SECONDS: integer(86_400, 60, 60 * 60 * 24 * 30),
    /**
     * The ceiling a sliding session can never be extended past, counted from when it was first
     * created. Without it, a session that is used every day is renewed forever and the TTL stops
     * meaning anything.
     */
    SESSION_ABSOLUTE_MAX_SECONDS: integer(60 * 60 * 24 * 30, 60, 60 * 60 * 24 * 365),
    SESSION_SECRET: optionalText,
    COOKIE_SECURE: flag(false),
    // --- ingestion ---
    DATA_DIR: text('./data'),
    TRANSCRIPTS_DIR: text('./data/transcripts'),
    CATALOG_XLSX: text('./data/catalog.xlsx'),
})
    .superRefine((value, ctx) => {
    // An absolute cap below the per-session TTL would expire every session the moment it was
    // issued, which presents as "login succeeds and the next request is a 401".
    if (value.SESSION_ABSOLUTE_MAX_SECONDS < value.SESSION_TTL_SECONDS) {
        ctx.addIssue({
            code: 'custom',
            path: ['SESSION_ABSOLUTE_MAX_SECONDS'],
            message: 'must be greater than or equal to SESSION_TTL_SECONDS',
        });
    }
    if (value.NODE_ENV !== 'production')
        return;
    if (value.SESSION_SECRET === undefined || value.SESSION_SECRET.length < 32) {
        ctx.addIssue({
            code: 'custom',
            path: ['SESSION_SECRET'],
            message: 'is required when NODE_ENV=production and must be at least 32 characters',
        });
    }
    if (!value.COOKIE_SECURE) {
        ctx.addIssue({
            code: 'custom',
            path: ['COOKIE_SECURE'],
            message: 'must be true when NODE_ENV=production so the session cookie is only sent over HTTPS',
        });
    }
});
/** Renders every Zod issue as one `KEY: message` line, sorted for stable output. */
export function formatEnvIssues(error) {
    return error.issues
        .map((issue) => {
        const key = issue.path.map((segment) => String(segment)).join('.');
        // Zod words a missing value as "expected string, received undefined", which reads as a
        // type error rather than as the missing configuration it actually is.
        const message = issue.code === 'invalid_type' && issue.input === undefined
            ? 'is required but not set'
            : issue.message;
        return key.length > 0 ? `${key}: ${message}` : message;
    })
        .sort((a, b) => a.localeCompare(b));
}
/** Pure: validates an arbitrary environment bag without touching `process`. */
export function parseEnv(source) {
    const result = envSchema.safeParse(source);
    return result.success
        ? { ok: true, env: result.data }
        : { ok: false, errors: formatEnvIssues(result.error) };
}
//# sourceMappingURL=env-schema.js.map